import { DndContext, DragOverlay } from '@dnd-kit/core'
import { ChevronRight, Eye, EyeOff, Info, Loader2, Save, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import { Drawer } from '@/components/ui/drawer'
import { confirmModal } from '@/components/ui/modal'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useSaveDashboardLayout } from '../api'
import { RoleDefaultControl } from './RoleDefaultControl'
import { defaultWidgets, sanitizeWidgets } from '../layout/model'
import type { WidgetConfig } from '../types'
import { WidgetRenderer } from '../widgets/WidgetRenderer'
import { EditorCanvas, type Device } from './EditorCanvas'
import { useEditorDnd } from './useEditorDnd'
import { clearDraft, useLayoutEditor } from './useLayoutEditor'
import { useMediaQuery } from './useMediaQuery'
import { LibraryCard, WidgetLibrary } from './WidgetLibrary'
import { WidgetProperties } from './WidgetProperties'

export function LayoutEditor({ userId, saved }: { userId: string; saved: WidgetConfig[] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const navigate = useNavigate()
  const editor = useLayoutEditor(userId, saved)
  const dnd = useEditorDnd(editor)
  const save = useSaveDashboardLayout()
  const isXl = useMediaQuery('(min-width: 1280px)')
  const [device, setDevice] = useState<Device>('desktop')
  const [preview, setPreview] = useState(false)
  const [showInfo, setShowInfo] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { selected } = editor
  const title = (type: WidgetConfig['type']) => t(`dashboard.widgets.${type}.title`)

  async function onCancel() {
    if (editor.dirty && !(await confirmModal({ title: t('dashboard.edit.discardTitle'), description: t('dashboard.edit.discardBody'), confirmLabel: t('dashboard.edit.discard'), destructive: true }))) return
    clearDraft(userId)
    navigate('/')
  }

  function onSave() {
    save.mutate(editor.widgets, {
      onSuccess: () => {
        clearDraft(userId)
        toast.success(t('dashboard.edit.saved'))
        navigate('/')
      },
      onError: (err) => toast.error(apiErrorMessage(err)),
    })
  }

  async function onRemove(id: string) {
    const widget = editor.widgets.find((w) => w.id === id)
    if (!widget) return
    const ok = await confirmModal({
      title: t('dashboard.edit.removeTitle'),
      description: t('dashboard.edit.removeBody', { title: title(widget.type) }),
      confirmLabel: t('dashboard.edit.remove'),
      destructive: true,
    })
    if (ok) editor.remove(id)
  }

  async function onSuggested() {
    if (!(await confirmModal({ title: t('dashboard.edit.suggestedTitle'), description: t('dashboard.edit.suggestedBody'), confirmLabel: t('dashboard.edit.apply') }))) return
    editor.setWidgets(sanitizeWidgets(defaultWidgets(), can))
    editor.select(null)
  }

  const properties = (
    <WidgetProperties
      widget={selected}
      onChange={(patch) => selected && editor.update(selected.id, patch)}
      onReset={() => selected && editor.reset(selected.id)}
      onRemove={() => selected && void onRemove(selected.id)}
      onClose={() => {
        setDrawerOpen(false)
        editor.select(null)
      }}
    />
  )

  const activeWidget = dnd.active?.kind === 'widget' ? editor.widgets.find((w) => w.id === (dnd.active as { id: string }).id) : undefined

  return (
    <>
      <PageHeader
        backTo="/"
        onBack={() => void onCancel()}
        eyebrow={
          <nav aria-label={t('dashboard.edit.breadcrumb')} className="flex items-center gap-1">
            <Link to="/" className="hover:text-foreground">{t('nav.dashboard')}</Link>
            <ChevronRight className="size-3" aria-hidden />
            <span className="text-foreground">{t('dashboard.edit.title')}</span>
          </nav>
        }
        title={t('dashboard.edit.title')}
        description={t('dashboard.edit.subtitle')}
        actions={
          <>
            {can('dashboard:layout:manage') ? <RoleDefaultControl widgets={editor.widgets} /> : null}
            <Button variant="outline" onClick={() => void onCancel()}>{t('dashboard.edit.cancel')}</Button>
            <Button variant="outline" aria-pressed={preview} onClick={() => setPreview((p) => !p)}>
              {preview ? <EyeOff /> : <Eye />}
              {t(preview ? 'dashboard.edit.exitPreview' : 'dashboard.edit.preview')}
            </Button>
            <Button onClick={onSave} disabled={save.isPending || !editor.dirty}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
              {t('common.save')}
            </Button>
          </>
        }
      />

      {showInfo ? (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-primary/15 bg-primary/[0.05] px-3.5 py-2.5 text-[13px] dark:bg-primary/10">
          <Info className="size-4 shrink-0 text-primary" aria-hidden />
          <p className="min-w-0 flex-1 text-muted-foreground">{t('dashboard.edit.info')}</p>
          <Button variant="ghost" size="icon" className="size-7" aria-label={t('common.close')} onClick={() => setShowInfo(false)}>
            <X />
          </Button>
        </div>
      ) : null}

      <DndContext sensors={dnd.sensors} collisionDetection={dnd.collisionDetection} {...dnd.handlers}>
        <div
          className={
            preview
              ? 'grid'
              : 'grid items-start gap-4 lg:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[260px_minmax(0,1fr)_300px]'
          }
        >
          {!preview ? <WidgetLibrary addedTypes={new Set(editor.widgets.map((w) => w.type))} onAdd={(type) => editor.add(type)} /> : null}
          <EditorCanvas
            editor={editor}
            placeholderId={dnd.placeholderId}
            dragging={dnd.active?.kind === 'library'}
            device={device}
            onDeviceChange={setDevice}
            preview={preview}
            onSuggested={() => void onSuggested()}
            onRemove={(id) => void onRemove(id)}
            onOpenProperties={!isXl && selected ? () => setDrawerOpen(true) : undefined}
          />
          {!preview && isXl ? (
            <aside className="sticky top-3 max-h-[calc(100dvh-7rem)] min-h-96 overflow-hidden rounded-2xl border bg-card">{properties}</aside>
          ) : null}
        </div>

        <DragOverlay>
          {dnd.active?.kind === 'library' ? <LibraryCard type={dnd.active.type} dragging /> : null}
          {activeWidget ? (
            <div className="h-full rotate-[0.6deg] rounded-2xl opacity-95 shadow-[0_24px_48px_-12px_color-mix(in_oklab,var(--shadow-color)_35%,transparent)] ring-2 ring-primary/50">
              <div inert className="h-full">
                <WidgetRenderer config={activeWidget} />
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {!preview && !isXl ? (
        <Drawer open={drawerOpen && !!selected} onOpenChange={setDrawerOpen} title={t('dashboard.edit.properties')} side="right" className="w-80 bg-card">
          {properties}
        </Drawer>
      ) : null}
    </>
  )
}
